import React from "react";

import {
  Button,
  TextField,
  Typography,
  Stack,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  type SelectChangeEvent,
} from "@mui/material";
import { HexColorPicker } from "react-colorful";
import DeleteIcon from "@mui/icons-material/Delete";

import { MeetingView, Organisation, UserType } from "../../../../types";
import {
  useUpdateOrganisation,
  useDeleteOrganisation,
  useGetCurrentUser,
  useGetUserWithIds,
} from "../../../../store";

import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { userHasPermission } from "../../../../types-helpers";
import TeamChip from "../../../../components/chips/team-chip";

interface GeneralSectionProps {
  team: Organisation;
}

const GeneralSection: React.FC<GeneralSectionProps> = ({ team }) => {
  const navigate = useNavigate();
  const { data: currentUser } = useGetCurrentUser();
  const { data: users } = useGetUserWithIds(team.usersIds || []);

  // Check permissions
  const canDelete = userHasPermission(UserType.SysAdmin, currentUser);

  // Check if team can be deleted (no users and no sites)
  const hasUsers =
    users && users.some((user) => user !== null) && users.length > 0;

  const [teamName, setTeamName] = React.useState(team.name);
  const [teamColor, setTeamColor] = React.useState(
    team.color || "#1976d2",
  );
  const [tempColor, setTempColor] = React.useState(team.color || "#1976d2");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [colorPickerOpen, setColorPickerOpen] = React.useState(false);
  const [meetingViews, setMeetingViews] = React.useState<MeetingView[]>(
    team.meetingViews || [],
  );

  const onUpdateSuccess = () => {
    toast.success("Team updated successfully");
  };

  const onUpdateError = (err: unknown) => {
    console.log(err);
    toast.error("Failed to update team");
  };

  const onDeleteSuccess = () => {
    toast.success("Team deleted successfully");
    navigate("/teams");
  };

  const onDeleteError = (err: unknown) => {
    console.log(err);
    toast.error("Failed to delete team");
  };

  const { mutate: updateTeam } = useUpdateOrganisation(
    onUpdateSuccess,
    onUpdateError,
  );
  const { mutate: deleteTeam } = useDeleteOrganisation(
    onDeleteSuccess,
    onDeleteError,
  );

  const meetingViewsEqual = (a: MeetingView[], b: MeetingView[]) => {
    if (a.length !== b.length) {
      return false;
    }

    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    return sortedA.every((view, index) => view === sortedB[index]);
  };

  const handleMeetingViewsChange = (
    event: SelectChangeEvent<MeetingView[]>,
  ) => {
    const { value } = event.target;
    setMeetingViews(
      typeof value === "string" ? (value.split(",") as MeetingView[]) : value,
    );
  };

  const handleUpdate = () => {
    if (teamName.trim() === "") {
      toast.error("Team name cannot be empty");
      return;
    }

    updateTeam({
      input: { name: teamName, meetingViews },
      organisationId: team.organisationId,
    });
  };

  const handleUpdateColor = () => {
    setTeamColor(tempColor);
    updateTeam({
      input: { color: tempColor },
      organisationId: team.organisationId,
    });
    setColorPickerOpen(false);
  };

  const handleColorChange = (color: string) => {
    setTempColor(color);
  };

  const handleColorPickerOpen = () => {
    setTempColor(teamColor);
    setColorPickerOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteTeam({ organisationId: team.organisationId });
    setDeleteConfirmOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  return (
    <Stack
      spacing={2}
      sx={{
        boxShadow: 3,
        p: 3,
        borderRadius: 2,
        backgroundColor: "background.paper",
      }}
    >
      {/* Team Name and Color */}
      <Stack direction="row" spacing={2} alignItems="flex-start">
        {/* Team Name */}
        <Stack spacing={1} flex={1}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Name"
              size="small"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              variant="outlined"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="meeting-views-label">Meeting Layouts</InputLabel>
              <Select
                labelId="meeting-views-label"
                multiple
                value={meetingViews}
                onChange={handleMeetingViewsChange}
                input={<OutlinedInput label="Meeting Layouts" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {Object.values(MeetingView).map((view) => (
                  <MenuItem key={view} value={view} dense>
                    <Checkbox
                      checked={meetingViews.includes(view)}
                      size="small"
                    />
                    <ListItemText
                      primary={view}
                      slotProps={{ primary: { fontSize: "0.875rem" } }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={
                teamName.trim() === "" ||
                (teamName === team.name &&
                  meetingViewsEqual(meetingViews, team.meetingViews || []))
              }
            >
              Update
            </Button>
          </Stack>
        </Stack>
        <Divider orientation="vertical" flexItem />
        {/* Team Color */}
        <Stack spacing={1} flex={1} justifyContent="center" sx={{ pt: 0.5, maxWidth: 200 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Colour:
            </Typography>
            <Chip
              label={teamColor.toUpperCase()}
              onClick={handleColorPickerOpen}
              sx={{
                backgroundColor: teamColor,
                fontWeight: 600,
                flex: 1,
              }}
            />
          </Stack>
        </Stack>
      </Stack>
      <Divider />
      <Stack
        spacing={1}
        direction={"row"}
        alignItems="center"
        justifyContent={"space-between"}
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: "rgb(255, 229, 229)",
        }}
      >
        <Stack
          sx={{ flexGrow: 1 }}
          alignItems="flex-start"
          justifyContent={"center"}
        >
          <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
            Danger Zone
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deleting a team is permanent and cannot be undone. All
            associated data will be marked as deleted.
          </Typography>
        </Stack>
        <Stack>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={!canDelete || hasUsers}
          >
            Delete Team
          </Button>
          <Typography
            variant="caption"
            color="error"
            sx={{
              display: "block",
              textAlign: "right",
              fontSize: "0.75rem",
            }}
          >
            {!canDelete &&
              " You do not have permission to delete this team."}
            {canDelete &&
              hasUsers &&
              " Cannot delete an team with users."}
          </Typography>
        </Stack>
      </Stack>

      {/* Color Picker Dialog */}
      <Dialog
        open={colorPickerOpen}
        onClose={() => setColorPickerOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Choose Color</DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            sx={{ p: 2, display: "flex", justifyContent: "center" }}
          >
            <HexColorPicker
              color={tempColor}
              onChange={handleColorChange}
              style={{ width: "100%", height: "250px" }}
            />
            <Chip
              label={tempColor.toUpperCase()}
              sx={{
                backgroundColor: tempColor,
                fontWeight: 600,
                width: "100%",
                fontSize: "1rem",
                height: "40px",
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setColorPickerOpen(false)}
            variant="outlined"
            fullWidth
          >
            Close
          </Button>
          <Button onClick={handleUpdateColor} variant="contained" fullWidth>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle component="div">
          <Typography variant="h3">Delete Team</Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1">
              Are you sure you want to delete the following team?
            </Typography>
            <TeamChip
              teamId={team.organisationId}
              name={team.name}
              color={team.color}
            />
            <Typography variant="body1">
              <b>This action cannot be undone.</b>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            size="small"
            fullWidth
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            size="small"
            variant="contained"
            fullWidth
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default GeneralSection;
